import axios from 'axios';

// Store actual backend URL for error messages
let actualBackendURL: string;

// Create axios instance with base URL
// Base URL includes /api, so individual API calls should NOT include /api prefix
const getBaseURL = () => {
  // Priority: REACT_APP_API_URL > REACT_APP_USE_PROXY > REACT_APP_API_ENV > default

  // Use proxy if enabled (bypasses CORS by proxying through React dev server)
  if (process.env.REACT_APP_USE_PROXY === 'true') {
    // Use relative path - setupProxy.js will proxy /api/* to backend
    return '/api';
  }

  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Support environment-based selection
  const apiEnv = process.env.REACT_APP_API_ENV;
  if (apiEnv === 'production' || apiEnv === 'prod') {
    return 'https://www.bonyad-hub.com/api';
  }
  if (apiEnv === 'staging' || apiEnv === 'eb') {
    return 'https://aac2o4ebrc.execute-api.me-central-1.amazonaws.com/api';
  }

  // Default: Production URL
  // All API calls are relative paths without /api (e.g., /auth/login, /users, etc.)
  return 'https://www.bonyad-hub.com/api';
};

const baseURL = getBaseURL();
const isUsingProxy = process.env.REACT_APP_USE_PROXY === 'true';
// Determine actual backend URL for proxy mode
if (isUsingProxy) {
  const apiEnv = process.env.REACT_APP_API_ENV;
  if (apiEnv === 'production' || apiEnv === 'prod') {
    actualBackendURL = 'https://www.bonyad-hub.com/api';
  } else {
    actualBackendURL = 'https://aac2o4ebrc.execute-api.me-central-1.amazonaws.com/api';
  }
} else {
  actualBackendURL = baseURL;
}

console.log('🌐 Admin Dashboard API Base URL:', baseURL);
if (isUsingProxy) {
  console.log('🔄 Using proxy mode - CORS bypassed via React dev server');
  console.log('📡 Actual backend URL:', actualBackendURL);
  console.log('💡 Requests to /api/* are proxied to:', actualBackendURL);
} else {
  console.log('⚠️  Direct connection - If you get CORS errors, use: npm run start:staging:proxy');
  console.log('📡 Backend URL:', actualBackendURL);
}

const api = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 30 second timeout
});

// Add token and headers to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    const cleanToken = token.trim();
    config.headers.Authorization = `Bearer ${cleanToken}`;
    console.log('🔐 [API Request]', config.method?.toUpperCase(), config.url, '- Token attached');
  } else {
    console.warn('⚠️ [API Request]', config.method?.toUpperCase(), config.url, '- No token found');
  }

  // Log request data for POST/PUT requests (for debugging)
  if ((config.method === 'post' || config.method === 'put') && config.data) {
    if (config.data instanceof FormData) {
      console.log('📤 [Request Data] FormData with fields:', Array.from(config.data.keys()));
    } else {
      console.log('📤 [Request Data]', JSON.stringify(config.data, null, 2));
    }
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Show actual backend URL in errors
    if (error.config) {
      const requestURL = error.config.url || error.config.baseURL || 'unknown';
      const fullURL = error.config.baseURL ? `${error.config.baseURL}${error.config.url || ''}` : requestURL;
      console.error('❌ Request failed:', error.config.method?.toUpperCase(), fullURL);
      if (isUsingProxy) {
        const proxiedURL = actualBackendURL + (error.config.url || '');
        console.error('📡 Proxied to backend:', proxiedURL);
        console.error('💡 (Browser shows https://www.bonyad-hub.com, but request goes to:', proxiedURL + ')');
      } else {
        console.error('📡 Backend URL:', actualBackendURL);
      }
    }

    // Check for CORS errors
    const isCorsError = error.message && (
      error.message.includes('CORS') ||
      error.message.includes('blocked') ||
      error.message.includes('Access-Control-Allow-Origin') ||
      (error.code === 'ERR_NETWORK' && error.message.includes('Failed'))
    );

    if (isCorsError || (error.code === 'ERR_NETWORK' && !error.response)) {
      console.error('🚫 CORS Error Detected!');
      console.error('❌ Error:', error.message || 'Network request failed');
      console.error('');
      console.error('💡 SOLUTION: Use proxy to bypass CORS');
      console.error('   1. Stop the current server (Ctrl+C)');
      console.error('   2. Run: npm run start:staging:proxy');
      console.error('   3. This will proxy requests and bypass CORS completely');
      console.error('');
      console.error('📝 Note: Proxy only works in development.');
      console.error('   For production, deploy backend with CORS fix.');
    } else if (error.response) {
      // Server responded with error status
      console.error('❌ Server Error:', error.response.status, error.response.statusText);
      console.error('📡 Backend URL:', actualBackendURL);
      if (error.response.data) {
        console.error('📄 Response:', error.response.data);
      }
    } else if (error.code === 'ERR_CONNECTION_CLOSED' || error.code === 'ERR_NETWORK') {
      console.error('❌ Connection error:', error.message);
      console.error('📡 Backend URL:', actualBackendURL);
      if (window.location.protocol === 'http:') {
        console.error('💡 Make sure backend is reachable at:', actualBackendURL);
        console.error('💡 If CORS error, use: npm run start:staging:proxy');
      } else {
        console.error('💡 Check Nginx proxy configuration for /api endpoint');
      }
    }

    return Promise.reject(error);
  }
);

// Login API
export const login = async (phoneNumber: string, password: string, role: string = 'ADMIN') => {
  const response = await api.post('/auth/login', {
    phoneNumber,
    password,
    role,
  });
  return response.data;
};

// Get all users
export const getAllUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

// Get all projects
export const getAllProjects = async () => {
  const response = await api.get('/projects');
  return response.data;
};

// Get dashboard statistics
export const getDashboardStats = async () => {
  try {
    const response = await api.get('/admin/support/dashboard/stats');
    // API returns: { success: true, totalTickets, resolvedTickets, activeChatRequests, pendingTickets }
    // or on error: { success: false, error: "..." }
    return response.data;
  } catch (error: any) {
    // Handle error response from API
    if (error.response && error.response.data) {
      // Return error response in API format
      return {
        success: false,
        error: error.response.data.error || error.response.data.message || 'Failed to fetch dashboard stats',
      };
    }
    // Re-throw for axios interceptor to handle
    throw error;
  }
};

// Get pending technicians
export const getPendingTechnicians = async () => {
  const response = await api.get('/admin/technicians/pending');
  return response.data;
};

// Request additional data from technician
export const requestAdditionalData = async (data: {
  technicianId: number;
  message: string;
  requestedFileTypes: string[];
}) => {
  const response = await api.post('/admin/technicians/request-data', data);
  return response.data;
};

// Approve technician
export const approveTechnician = async (technicianId: number) => {
  const response = await api.post(`/admin/technicians/${technicianId}/approve`);
  return response.data;
};

// Get technician regions
export const getTechnicianRegions = async (technicianId: number) => {
  const response = await api.get(`/admin/technicians/${technicianId}/regions`);
  return response.data;
};

// Add regions to technician
export const addRegionsToTechnician = async (technicianId: number, regionIds: number[]) => {
  const response = await api.post(`/admin/technicians/${technicianId}/regions/add`, {
    regionIds
  });
  return response.data;
};

// Remove regions from technician
export const removeRegionsFromTechnician = async (technicianId: number, regionIds: number[]) => {
  const response = await api.post(`/admin/technicians/${technicianId}/regions/remove`, {
    regionIds
  });
  return response.data;
};

// Get technician certificates and admin requests
export const getTechnicianDetails = async (technicianId: number) => {
  // Get user details which includes certificates
  const userResponse = await api.get(`/users/${technicianId}`);
  const user = userResponse.data;

  // Get admin data requests for this technician
  const dataRequestsResponse = await api.get('/admin/data-requests');
  const allRequests = dataRequestsResponse.data || [];
  const technicianRequests = allRequests.filter((req: any) =>
    req.technicianId === technicianId || req.technician?.id === technicianId
  );

  return {
    certificates: user.certificates || [],
    adminRequests: technicianRequests
  };
};

// Get all data requests
export const getAllDataRequests = async (status?: string) => {
  const params = status ? { status } : {};
  const response = await api.get('/admin/data-requests', { params });
  return response.data;
};

// Send notification
export const sendNotification = async (notificationData: {
  // Legacy single-language fields
  title?: string;
  body?: string;
  // Bilingual fields (recommended)
  titleAr?: string;
  titleEn?: string;
  bodyAr?: string;
  bodyEn?: string;
  // Targeting options
  userId?: number;
  sendToAllUsers?: boolean;
  sendToAllTechnicians?: boolean;
  regionId?: number;
  subscriptionCategoryId?: number;
  serviceId?: number;
  data?: Record<string, string>;
}) => {
  const response = await api.post('/admin/notifications/send', notificationData);
  return response.data;
};

// Get regions (for notification targeting)
export const getRegions = async () => {
  const response = await api.get('/regions');
  return response.data;
};

// Get services (for notification targeting)
export const getServices = async () => {
  const response = await api.get('/services');
  return response.data;
};

// Get subscription categories (for notification targeting)
export const getSubscriptionCategories = async () => {
  const response = await api.get('/subscriptions/categories');
  return response.data;
};

// Get online users count
export const getOnlineUsersCount = async () => {
  const response = await api.get('/admin/users/online/count');
  return response.data;
};

// Get all online users
export const getOnlineUsers = async () => {
  const response = await api.get('/admin/users/online');
  return response.data;
};

// Get users with last seen timestamps
export const getUsersLastSeen = async () => {
  const response = await api.get('/admin/users/last-seen');
  return response.data;
};

// Get user details by ID (for MQTT users)
export const getUserById = async (userId: number) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data;
};

// Get all bids (admin)
export const getAllBids = async () => {
  const response = await api.get('/admin/bids');
  return response.data;
};

// Delete project (admin)
export const deleteProject = async (projectId: number, reason: string) => {
  const response = await api.delete(`/admin/projects/${projectId}`, {
    data: { reason }
  });
  return response.data;
};

// Delete bid (admin)
export const deleteBid = async (bidId: number, reason: string) => {
  const response = await api.delete(`/admin/bids/${bidId}`, {
    data: { reason }
  });
  return response.data;
};

// Check project dependencies
export const checkProjectDependencies = async (projectId: number) => {
  const response = await api.get(`/admin/projects/${projectId}/dependencies`);
  return response.data;
};

// Update project status (admin)
export const updateProjectStatus = async (projectId: number, status: string) => {
  const response = await api.put(`/admin/projects/${projectId}/status`, {
    status
  });
  return response.data;
};

// Get all change requests (admin)
export const getAllChangeRequests = async (projectId?: number, status?: string) => {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId.toString());
  if (status) params.append('status', status);

  const url = `/admin/change-requests${params.toString() ? '?' + params.toString() : ''}`;
  const response = await api.get(url);
  return response.data;
};

// Delete change request (admin)
export const deleteChangeRequest = async (changeRequestId: number, reason?: string) => {
  const response = await api.delete(`/admin/change-requests/${changeRequestId}`, {
    data: { reason: reason || 'Deleted by administrator' }
  });
  return response.data;
};

// Get upcoming appointments
export const getUpcomingAppointments = async () => {
  const response = await api.get('/admin/appointments/upcoming');
  return response.data;
};

// Analytics APIs
export const getFinancialAnalytics = async () => {
  const response = await api.get('/admin/analytics/financial');
  return response.data;
};

export const getSubscriptionAnalytics = async () => {
  const response = await api.get('/admin/analytics/subscriptions');
  return response.data;
};

export const getAdvancedAnalytics = async (months?: number) => {
  const params = months ? { months } : {};
  const response = await api.get('/admin/analytics/advanced', { params });
  return response.data;
};

export const getAlerts = async () => {
  const response = await api.get('/admin/analytics/alerts');
  return response.data;
};

export const getProjectsFinancial = async () => {
  const response = await api.get('/admin/analytics/projects-financial');
  return response.data;
};

// Get projects dashboard data
export const getProjectsDashboard = async () => {
  const response = await api.get('/admin/projects/dashboard');
  return response.data;
};

// Add: Get service user counts (users per service)
export const getServiceUserCounts = async () => {
  const response = await api.get('/admin/analytics/service-user-counts');
  return response.data as Array<{ serviceId: number; nameEn: string; nameAr: string; userCount: number }>;
};

// Subscription Categories Management APIs
export const getAllSubscriptionCategories = async () => {
  const response = await api.get('/subscriptions');
  return response.data;
};

export const createSubscriptionCategory = async (category: {
  nameAr: string;
  nameEn: string;
  price: number;
  descriptionAr: string;
  descriptionEn: string;
  durationDays: number;
  isActive: boolean;
  bidsPerWeek?: number;
  discountPercentage?: number;
  discountStartDate?: string;
  discountEndDate?: string;
}) => {
  const response = await api.post('/subscriptions', category);
  return response.data;
};

export const updateSubscriptionCategory = async (
  id: number,
  category: {
    nameAr: string;
    nameEn: string;
    price: number;
    descriptionAr: string;
    descriptionEn: string;
    durationDays: number;
    isActive: boolean;
    bidsPerWeek?: number;
    discountPercentage?: number;
    discountStartDate?: string;
    discountEndDate?: string;
  }
) => {
  const response = await api.put(`/subscriptions/${id}`, category);
  return response.data;
};

export const deleteSubscriptionCategory = async (id: number) => {
  const response = await api.delete(`/subscriptions/${id}`);
  return response.data;
};

// Moderation Violations APIs
export const getModerationViolations = async (params?: {
  reviewed?: boolean;
  userId?: number;
}) => {
  const response = await api.get('/admin/moderation/violations', { params });
  return response.data;
};

export const reviewViolation = async (violationId: number, actionTaken: string = 'REVIEWED') => {
  const response = await api.post(`/admin/moderation/violations/${violationId}/review`, {
    actionTaken
  });
  return response.data;
};

// Delete user (admin only)
export const deleteUser = async (userId: number) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

// Support Tickets APIs (Admin)
export const getAllTickets = async (params?: {
  status?: string;
  priority?: string;
  category?: string;
  assignedAdminId?: number;
}) => {
  const response = await api.get('/admin/support/tickets', { params });
  return response.data;
};

export const getTicketById = async (ticketId: number) => {
  const response = await api.get(`/admin/support/tickets/${ticketId}`);
  return response.data;
};

export const addTicketMessage = async (ticketId: number, message: string, attachmentUrls?: string[]) => {
  const response = await api.post(`/admin/support/tickets/${ticketId}/messages`, {
    message,
    attachmentUrls: attachmentUrls || []
  });
  return response.data;
};

export const updateTicketStatus = async (ticketId: number, data: {
  status: string;
  priority?: string;
  assignToAdminId?: number;
}) => {
  const response = await api.put(`/admin/support/tickets/${ticketId}/status`, data);
  return response.data;
};

export const closeTicket = async (ticketId: number) => {
  const response = await api.put(`/admin/support/tickets/${ticketId}/close`);
  return response.data;
};

export const getTicketStats = async () => {
  const response = await api.get('/admin/support/stats');
  return response.data;
};

// Get interactions timeline between user and technician (or all logs for a user if technicianId is not provided)
export const getUserTechnicianInteractions = async (userId: number, technicianId?: number, startDate?: string, endDate?: string) => {
  const params: any = { userId };
  if (technicianId !== undefined) {
    params.technicianId = technicianId;
  }
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  const response = await api.get('/admin/interactions', { params });
  return response.data;
};

// ========================================
// AUDIT LOG APIs
// ========================================

export interface AuditLogFilters {
  eventType?: string;
  entityType?: string;
  entityId?: number;
  projectId?: number;
  performedById?: number;
  userId?: number;
  technicianId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}

export interface AuditLog {
  id: number;
  eventType: string;
  performedById: number;
  performedByName?: string;
  userId?: number;
  userName?: string;
  technicianId?: number;
  technicianName?: string;
  entityType?: string;
  entityId?: number;
  projectId?: number;
  timestamp: string;
  description: string;
  oldValue?: any;
  newValue?: any;
  metadata?: Record<string, any>;
  ipAddress?: string;
}

export interface PaginatedAuditLogs {
  content: AuditLog[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
}

// Get all audit logs with filters
export const getAuditLogs = async (filters?: AuditLogFilters): Promise<PaginatedAuditLogs> => {
  const params: any = {};
  if (filters?.eventType) params.eventType = filters.eventType;
  if (filters?.entityType) params.entityType = filters.entityType;
  if (filters?.entityId) params.entityId = filters.entityId;
  if (filters?.projectId) params.projectId = filters.projectId;
  if (filters?.performedById) params.performedById = filters.performedById;
  if (filters?.userId) params.userId = filters.userId;
  if (filters?.technicianId) params.technicianId = filters.technicianId;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  if (filters?.page !== undefined) params.page = filters.page;
  if (filters?.size !== undefined) params.size = filters.size;

  const response = await api.get('/admin/audit-logs', { params });
  const data = response.data;

  // Handle both paginated and non-paginated responses
  if (data.content) {
    return data;
  }
  return {
    content: Array.isArray(data) ? data : [],
    totalElements: Array.isArray(data) ? data.length : 0,
    totalPages: 1,
    currentPage: filters?.page || 0,
    size: filters?.size || 20,
  };
};

// Get audit logs by entity
export const getAuditLogsByEntity = async (entityType: string, entityId: number): Promise<AuditLog[]> => {
  const response = await api.get('/admin/audit-logs', {
    params: { entityType, entityId }
  });
  return Array.isArray(response.data) ? response.data : (response.data.content || []);
};

// Get audit logs by project ID
export const getAuditLogsByProject = async (projectId: number): Promise<AuditLog[]> => {
  const response = await api.get('/admin/audit-logs', {
    params: { projectId }
  });
  return Array.isArray(response.data) ? response.data : (response.data.content || []);
};

// Get audit logs by event type
export const getAuditLogsByEventType = async (eventType: string, page?: number, size?: number): Promise<PaginatedAuditLogs> => {
  const params: any = { eventType };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;

  const response = await api.get('/admin/audit-logs', { params });
  const data = response.data;

  if (data.content) {
    return data;
  }
  return {
    content: Array.isArray(data) ? data : [],
    totalElements: Array.isArray(data) ? data.length : 0,
    totalPages: 1,
    currentPage: page || 0,
    size: size || 20,
  };
};

// Get audit logs by date range
export const getAuditLogsByDateRange = async (startDate: string, endDate: string, page?: number, size?: number): Promise<PaginatedAuditLogs> => {
  const params: any = { startDate, endDate };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;

  const response = await api.get('/admin/audit-logs', { params });
  const data = response.data;

  if (data.content) {
    return data;
  }
  return {
    content: Array.isArray(data) ? data : [],
    totalElements: Array.isArray(data) ? data.length : 0,
    totalPages: 1,
    currentPage: page || 0,
    size: size || 20,
  };
};

// Get all available event types
export const getAuditEventTypes = async (): Promise<string[]> => {
  const response = await api.get('/admin/audit-logs/event-types');
  return Array.isArray(response.data) ? response.data : [];
};

// Create user (Admin only - creates user with APPROVED status)
export const createUser = async (userData: {
  name: string;
  phoneNumber: string;
  email?: string;
  password: string;
  role: 'ADMIN' | 'USER' | 'TECHNICIAN';
  status?: 'PENDING' | 'APPROVED' | 'SUSPENDED' | 'WAITING_ADMIN_APPROVAL';
  profileComplete?: boolean;
  onboarded?: boolean;
  hasPortfolio?: boolean;
  isOnline?: boolean;
  profileImage?: string;
  description?: string;
  yearsOfExperience?: number;
  certificates?: string[];
  regionIds?: number[];
  serviceIds?: number[];
  subscriptionCategoryId?: number;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  availabilityStatus?: 'FIXED_TIMES' | 'AVAILABLE_ANYTIME';
  deviceToken?: string;
  lastSeen?: string;
  socketSessionId?: string;
}) => {
  const response = await api.post('/admin/users/create', userData);
  return response.data;
};

// Phone Change Requests
export const getPendingPhoneChangeRequests = async () => {
  const response = await api.get('/admin/phone-change-requests');
  return response.data;
};

export const approvePhoneChangeRequest = async (requestId: number, reason?: string) => {
  const response = await api.post(`/admin/phone-change-requests/${requestId}/approve`, {
    reason: reason || 'Approved by admin - OTP not sent or failed'
  });
  return response.data;
};

// Maintenance Mode APIs (Enum-Based)
export const getMaintenanceStatus = async () => {
  const response = await api.get('/admin/maintenance/status');
  return response.data;
};

// Unified endpoint to set maintenance mode
export const setMaintenanceMode = async (
  mode: 'DISABLED' | 'REGISTER_ONLY' | 'FULL_LOCKDOWN',
  messageEn?: string,
  messageAr?: string
) => {
  const body: any = { mode };
  if (messageEn) body.messageEn = messageEn;
  if (messageAr) body.messageAr = messageAr;

  const response = await api.post('/admin/maintenance/set-mode', body);
  return response.data;
};

// Update messages only (without changing mode)
export const updateMaintenanceMessages = async (messageEn: string, messageAr: string) => {
  const response = await api.put('/admin/maintenance/update-messages', {
    messageEn,
    messageAr
  });
  return response.data;
};

// Small Tasks APIs
export const getSmallTaskTypes = async () => {
  const response = await api.get('/admin/small-tasks/types');
  return response.data;
};

export const createSmallTaskType = async (taskType: {
  nameAr: string;
  nameEn: string;
  descriptionAr?: string;
  descriptionEn?: string;
  icon?: string;
  isActive?: boolean;
}) => {
  const response = await api.post('/admin/small-tasks/types', taskType);
  return response.data;
};

export const updateSmallTaskType = async (id: number, taskType: {
  nameAr?: string;
  nameEn?: string;
  descriptionAr?: string;
  descriptionEn?: string;
  icon?: string;
  isActive?: boolean;
}) => {
  const response = await api.put(`/admin/small-tasks/types/${id}`, taskType);
  return response.data;
};

export const deleteSmallTaskType = async (id: number) => {
  const response = await api.delete(`/admin/small-tasks/types/${id}`);
  return response.data;
};

export const toggleSmallTaskType = async (id: number) => {
  const response = await api.patch(`/admin/small-tasks/types/${id}/toggle`);
  return response.data;
};

export const getSmallTaskRequests = async (status?: string) => {
  const params = status ? { status } : {};
  const response = await api.get('/admin/small-tasks/requests', { params });
  return response.data;
};

export const deleteSmallTaskRequest = async (id: number, reason: string) => {
  const response = await api.delete(`/admin/small-tasks/requests/${id}`, {
    data: { reason }
  });
  return response.data;
};

// ========================================
// TERMS & CONDITIONS APIs
// ========================================

export interface TermsAndCondition {
  id: number;
  contentAr: string;
  contentEn: string;
  type: string;
  version: string;
  isActive: boolean;
  createdById?: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTermsRequest {
  contentAr: string;
  contentEn: string;
  type: 'USER' | 'TECHNICIAN';
  version: string;
  isActive?: boolean;
}

export interface TermsAcceptanceLog {
  logId: number;
  userId?: number;
  userName?: string;
  userRole?: string;
  entityId?: number;
  description: string;
  metadata?: Record<string, any>;
  ipAddress?: string | null;
  acceptedAt?: string;
}

// Get all terms (admin) - optionally filter by type
export const getAllTerms = async (type?: string): Promise<{ terms: TermsAndCondition[]; count: number }> => {
  const params: any = {};
  if (type) params.type = type;
  const response = await api.get('/admin/terms', { params });
  return response.data;
};

// Get terms by ID (admin)
export const getTermsById = async (id: number): Promise<TermsAndCondition> => {
  const response = await api.get(`/admin/terms/${id}`);
  return response.data;
};

// Create terms
export const createTerms = async (data: CreateTermsRequest): Promise<TermsAndCondition> => {
  const response = await api.post('/admin/terms', data);
  return response.data;
};

// Update terms
export const updateTerms = async (id: number, data: CreateTermsRequest): Promise<TermsAndCondition> => {
  const response = await api.put(`/admin/terms/${id}`, data);
  return response.data;
};

// Soft delete terms (sets isActive = false)
export const deleteTerms = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/admin/terms/${id}`);
  return response.data;
};

// Hard delete terms (permanent)
export const permanentDeleteTerms = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/admin/terms/${id}/permanent`);
  return response.data;
};

// Get all acceptance logs (TERMS_ACCEPTED audit entries)
export const getTermsAcceptanceLogs = async (): Promise<{ logs: TermsAcceptanceLog[]; count: number }> => {
  const response = await api.get('/admin/terms/acceptance/logs');
  return response.data;
};

// Export all acceptance logs to Excel
export const exportTermsAcceptanceExcel = async (): Promise<Blob> => {
  const response = await api.get('/admin/terms/acceptance/export/excel', {
    responseType: 'blob',
  });
  return response.data;
};

export default api;

